import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	MotifButton,
	MotifProgressLoader,
	MotifErrorMessage,
} from "@ey-xd/motif-react";
import Tree from "@/components/Tree/Tree";
import { useForm, FormProvider } from "react-hook-form";
import {
	useGetClaimListAttributeQuery,
} from "@/services/advanceSearch/advanceSearchReducer";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { processTreeRows } from "@/utility/common";
import { APPLY_BUTTON_VARIANT, CANCEL_BUTTON_VARIANT } from "@/components/Images/CommonImages";

type AdvanceSearchData = Array<{ [key: string]: any }>;

interface AdvanceSearchProps {
	setAdvanceSearchData?: (data: AdvanceSearchData) => void;
	setShowAdvanceSearch: (show: boolean) => void;
	setIsAdvanceSearchActive: (show: boolean) => void;
	advanceSearchPayload: any;
	setAdvanceSearchPayload :(payload:any)=>void;
	handleReset:any
}

const AdvanceSearch = ({
	setShowAdvanceSearch,
	setIsAdvanceSearchActive,
	advanceSearchPayload,
	setAdvanceSearchPayload,
	handleReset
}: AdvanceSearchProps) => {
	const { t } = useTranslation();
	const { caseId } = useParams();
	const [isApplyLoading, setIsApplyLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [attributes, setAttributes] = useState([]);

	const methods = useForm({
		defaultValues: {
			logicalOperator: "0",
			treeRows: [],
		},
	});
	const {
		handleSubmit,
		formState: { errors },
		reset,
	} = methods;
	const errorList = Object.keys(errors);
	const {
    data: attributesData,
    isLoading,
    error,
  } = useGetClaimListAttributeQuery(caseId, { skip: !caseId });
  const watchTreeRows = methods.watch("treeRows");
  useEffect(() => {
    if (errorMessage) {
      if (watchTreeRows.length >= 0) {
        setErrorMessage("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchTreeRows]);
	useEffect(() => {
		if (attributesData) {
			setAttributes(attributesData);
		}
	}, [attributesData]);

	useEffect(() => {
		if (
			attributes.length > 0 &&
			advanceSearchPayload &&
			Object.keys(advanceSearchPayload).length > 0
		) {
			const transformRules = (rules: any[]) => {
				return rules.map((rule: any) => ({
					...rule,
					attribute: `${rule.entityName}.${rule.propertyName}`,
					type: "row",
				}));
			};

			const transformGroups = (groups: any[]): any => {
				return groups.map((group: any) => ({
					...group,
					logicalOperator: group.logicalOperator.toString(),
					treeRows: [...transformRules(group.rules)],
					type: "group",
				}));
			};
			const transformedData = {
				treeRows: [
					...transformRules(advanceSearchPayload.rules || []),
					...(Array.isArray(advanceSearchPayload.groups)
						? transformGroups(advanceSearchPayload.groups)
						: []),
				],
				logicalOperator: advanceSearchPayload.LogicalOperator.toString(),
			};
			reset(transformedData);
		}
	}, [advanceSearchPayload, reset, attributes]);

	if (isLoading) {
		return <MotifProgressLoader show variant="default" />;
	}
	if (error) {
		return <MotifErrorMessage>Data could not be loaded</MotifErrorMessage>;
	}
	const onSubmit = async (data: any) => {
		const { rules, groups } = processTreeRows(data.treeRows, caseId);
		if (watchTreeRows.length === 0) {
			setErrorMessage(t("pages.configuration.scheme.rule.noConditionAdded"));
			return;
		  } else {
			setErrorMessage("");
		  }
		const payload = {
			CaseId: caseId,
			rules,
			groups: groups.length > 0 ? groups : null,
			LogicalOperator: parseInt(data.logicalOperator),
		};
		setErrorMessage("");
		setIsApplyLoading(true);
		setAdvanceSearchPayload(payload)
		setShowAdvanceSearch(false);
		setIsAdvanceSearchActive(true);
		if(handleReset){
			handleReset(payload)
		}
	};

	return (
		<div className="claims-basic-search-container">
			{errorMessage && (
				<NotificationGrid show={true} message={errorMessage} type="error" onClose={()=>setErrorMessage("")} />
			)}
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Tree attributes={attributes} />
					<div className="error-container">
						<MotifErrorMessage>
							{errorList.length
								? t("pages.configuration.scheme.rule.validationError")
								: ""}
						</MotifErrorMessage>
					</div>
					<div className="basic-search-buttons">
						<MotifButton
							size="small"
							type="button"
							variant={CANCEL_BUTTON_VARIANT}
							onClick={() => {setShowAdvanceSearch(false)}}
						>
							{t("pages.claims.advancedSearch.cancel")}
						</MotifButton>

						<MotifButton
							size="small"
							variant={APPLY_BUTTON_VARIANT}
							type={isApplyLoading ? "loader" : "submit"}
							onClick={handleSubmit(onSubmit)}
						>
							{t("pages.claims.advancedSearch.apply")}
						</MotifButton>
					</div>
				</form>
			</FormProvider>
		</div>
	);
};
export default AdvanceSearch;
